import {ImageModel, ListModel, TimestampsModel} from "../common";
import {TemplateCompactModel} from "../templates";
import {AssistantSynthesizerCompactModel} from "./synthesizers";
import {WorkspaceThingModel} from "../workspace";
import {AssistantBackgroundNoiseCompactModel} from "./backgroundNoises";
import {AssistantExternalWebhookCompactModel} from "./externalWebhooks";
import {
  AssistantPhoneNumberCompactModel,
  AssistantPhoneNumberEvenCompactModel,
} from "./phoneNumbers";
import {AssistantPromptingTypeModel} from "./prompt";
import {AssistantReminderCompactModel} from "./reminders";
import {
  AssistantVoiceEvenCompactModel,
  AssistantVoiceCompactModel,
  AssistantVoiceLanguageModel,
  AssistantTranscriberProviderModel,
} from "./voices";
import {KnowledgeBaseRecordModel} from "../knowledge-bases";

export enum AssistantTypeModel {
  INBOUND = "inbound",
  OUTBOUND = "outbound",
  WIDGET = "widget",
}

export type AssistantRecordModel<
  T extends AssistantTypeModel = AssistantTypeModel,
> = TimestampsModel<true, true> & {
  id: string;
  name: string;
  type: T;
  voice: AssistantVoiceEvenCompactModel;
  avatar: ImageModel;
  phoneNumber: AssistantPhoneNumberEvenCompactModel;
  actionCount?: number;
};

export type AssistantListModel = ListModel<AssistantRecordModel>;

export type AssistantCompactModel<
  T extends AssistantTypeModel = AssistantTypeModel,
> = Pick<
  AssistantRecordModel<T>,
  "id" | "name" | "type" | "avatar" | "phoneNumber"
>;

export type AssistantCompactListModel = ListModel<AssistantCompactModel>;

export enum AssistantAiModel {
  SYNTHFLOW = "synthflow",
  GPT_4O = "gpt-4o",
  GPT_41 = "gpt-4.1",
  GPT_41_MINI = "gpt-4.1-Mini",
  GPT_5_CHAT = "gpt-5-chat",
}

export enum AssistantPatienceLevelModel {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

export enum AssistantSpeechRecognitionLevelModel {
  FASTER = "faster",
  HIGH_ACCURACY = "high_accuracy",
}

export type AssistantDetailedModel<
  T extends AssistantTypeModel = AssistantTypeModel,
> = TimestampsModel<false, true> &
  WorkspaceThingModel &
  AssistantRecordModel<T> & {
    // Basic Information
    id: string;
    name: string;
    type: T;
    avatar: ImageModel;

    // AI and Language Settings
    aiModel: AssistantAiModel;
    timezone: string;
    prompt: string;
    personality: string | null;
    temperature: number;
    transcriberKeywords: string[];
    transcriberProvider: AssistantTranscriberProviderModel;

    // Voice Configuration
    voice: AssistantVoiceCompactModel;
    synthesizer: AssistantSynthesizerCompactModel;
    patienceLevel: AssistantPatienceLevelModel;
    speechRecognitionLevel: AssistantSpeechRecognitionLevelModel;
    fadeOutAtInterruption: number;
    isKrispEnabled: boolean;
    voiceIsolationLevel: number;
    voiceSpeed: number;
    voiceVolume: number;
    minWordsToInterrupt: number;

    // Call Duration and Timing Settings
    initialPauseSeconds: number;
    ringDuration: number;
    maxDurationIsEnabled: boolean;
    maxDurationSeconds: number;
    allowedIdleTimeSeconds: number;

    // Reminder Settings
    reminder: AssistantReminderCompactModel;
    sendUserIdleReminder: boolean;

    // Call Recording and Transcription
    isRecording: boolean;
    isTranscriptEnabled: boolean;
    isDataRetentionEnabled: boolean;

    // Rate Limiting and Permissions
    rateLimitPerMinute: number;
    isLowRiskPermissionsDisabled: boolean;

    // External Integration Settings
    phoneNumber: AssistantPhoneNumberCompactModel;
    externalWebhook: AssistantExternalWebhookCompactModel;
    backgroundNoise: AssistantBackgroundNoiseCompactModel;
    sendTestCallWebhook: boolean;
    useAmdFromTelephonyProvider: boolean;
    sipDomain: string | null;
    sipOutboundTrunkId: string | null;
    urlWssWidget: string | null;

    // Miscellaneous Settings
    isPublic: boolean;
    isTemplate: boolean;
    greetingMessage: string;
    callerId: string | null;
    campaignId: string | null;
    language: AssistantVoiceLanguageModel;
    tier: string | null;
    knowledgeBase: KnowledgeBaseRecordModel | null;
    // Vector database is the same as knowledge base but in Bubble
    vectorDatabase: {id: string; name: string} | null;

    // Consent Settings
    isConsentEnabled: boolean;
    consentMessage: string;

    // Inbound Webhook
    inboundWebhookUrl: string | null;

    // Widget
    widgetId: string | null;
    widgetVoiceId: string | null;

    // Prompting Type
    promptingType: AssistantPromptingTypeModel;
    featureFlags: string[];
    memoryStoreId: string | null;
  };

// INFO: this matter because depending on initially selected flow
// the assistant detail page will be different
// TODO: use this model where missing.
export enum AssistantCreateFlowModel {
  FROM_SCRATCH = "from-scratch",
  QUICK_SETUP = "quick-setup",
  TEMPLATE = "TEMPLATE",
}

type AssistantCreateBaseModel<T extends AssistantCreateFlowModel, D> = {
  flow: T;
  data: D;
  // TODO: might become common model in the future.
  meta: {workspaceId: string; widgetId?: string};
};

export type AssistantCreateModel =
  | AssistantCreateBaseModel<
      AssistantCreateFlowModel.FROM_SCRATCH,
      {
        type: AssistantTypeModel;
      }
    >
  | AssistantCreateBaseModel<
      AssistantCreateFlowModel.TEMPLATE,
      {
        template: TemplateCompactModel;
      }
    >;

export type AssistantEvenCompactModel<
  T extends AssistantTypeModel = AssistantTypeModel,
> = Pick<AssistantRecordModel<T>, "id" | "name" | "type" | "avatar">;
